import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { formatMinor } from '../../lib/money';

interface Loan {
  id: string;
  accessionNumber: string;
  title: string;
  borrowerType: 'student' | 'staff';
  borrowerName: string;
  dueOn: string;
  status: string;
  overdueDays: number;
  renewals: number;
}
interface Person {
  id: string;
  firstName: string;
  lastName: string;
  admissionNumber?: string;
  employeeNumber?: string;
}

export function CirculationTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const issuing = can('library.issue', schoolId);
  const [accession, setAccession] = useState('');
  const [type, setType] = useState<'student' | 'staff'>('student');
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<Person | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const school = useQuery({
    queryKey: ['schools', schoolId],
    queryFn: () => api<{ currencyCode: string }>(`/schools/${schoolId}`),
  });
  const currency = school.data?.currencyCode ?? 'USD';
  const matches = useQuery({
    queryKey: ['library', schoolId, 'borrowers', type, search],
    queryFn: () =>
      apiList<Person>(type === 'student' ? '/students' : '/staff', {
        query: { schoolId, q: search, pageSize: 6 },
      }).then((r) => r.data),
    enabled: issuing && search.trim().length >= 2 && !person,
  });
  const loans = useQuery({
    queryKey: ['library', schoolId, 'loans', overdueOnly, page],
    queryFn: () =>
      apiList<Loan>(`/schools/${schoolId}/library/loans`, {
        query: { status: 'active', overdue: overdueOnly ? 'true' : undefined, page, pageSize: 20 },
      }),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['library', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });

  const issue = useMutation({
    mutationFn: () =>
      api<{ dueOn: string }>(`/schools/${schoolId}/library/loans`, {
        method: 'POST',
        body: { accessionNumber: accession.trim(), borrowerType: type, borrowerId: person!.id },
      }),
    onSuccess: (loan) => {
      setMessage({ kind: 'ok', text: t('lib.issued', { date: loan.dueOn }) });
      setAccession('');
      refresh();
    },
    onError: fail,
  });
  const returnLoan = useMutation({
    mutationFn: (id: string) =>
      api<{ fines: { amountMinor: number }[] }>(`/schools/${schoolId}/library/loans/${id}/return`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (loan) => {
      const fine = loan.fines.reduce((s, f) => s + f.amountMinor, 0);
      setMessage({
        kind: 'ok',
        text:
          fine > 0
            ? t('lib.returnedFine', { amount: formatMinor(fine, currency) })
            : t('lib.returned'),
      });
      refresh();
    },
    onError: fail,
  });
  const act = useMutation({
    mutationFn: (args: { id: string; what: 'renew' | 'lost' }) =>
      api(`/schools/${schoolId}/library/loans/${args.id}/${args.what}`, {
        method: 'POST',
        body: args.what === 'lost' ? {} : undefined,
      }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('lib.tab.circulation')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {issuing && (
        <form
          noValidate
          aria-label={t('lib.issue')}
          onSubmit={(e) => {
            e.preventDefault();
            if (accession.trim() && person) issue.mutate();
          }}
          className="grid items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
        >
          <TextField
            label={t('lib.accession')}
            value={accession}
            onChange={(e) => setAccession(e.target.value)}
          />
          <div>
            <label htmlFor="borrower-type" className="block text-sm font-medium">
              {t('lib.borrowerType')}
            </label>
            <select
              id="borrower-type"
              value={type}
              onChange={(e) => {
                setType(e.target.value as 'student' | 'staff');
                setPerson(null);
                setSearch('');
              }}
              className={selectClass}
            >
              <option value="student">{t('lib.student')}</option>
              <option value="staff">{t('lib.staff')}</option>
            </select>
          </div>
          <div className="relative">
            <TextField
              label={t('lib.findBorrower')}
              value={person ? `${person.firstName} ${person.lastName}` : search}
              onChange={(e) => {
                setPerson(null);
                setSearch(e.target.value);
              }}
            />
            {!person && matches.data && matches.data.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full divide-y divide-slate-100 rounded border border-slate-200 bg-white text-sm shadow">
                {matches.data.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="w-full p-2 text-left hover:bg-slate-50"
                      onClick={() => setPerson(p)}
                    >
                      {p.firstName} {p.lastName} · {p.admissionNumber ?? p.employeeNumber}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="submit"
            className={primaryButton}
            disabled={!person || !accession.trim() || issue.isPending}
          >
            {t('lib.issueBtn')}
          </button>
        </form>
      )}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('lib.activeLoans')}</h2>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => {
                setOverdueOnly(e.target.checked);
                setPage(1);
              }}
            />
            {t('lib.overdueOnly')}
          </label>
        </div>
        {loans.data?.data.length === 0 && <p className="text-slate-600">{t('lib.noLoans')}</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {loans.data?.data.map((l) => (
            <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{l.title}</span>{' '}
                <span className="font-mono text-xs">{l.accessionNumber}</span> · {l.borrowerName} ·{' '}
                {t('lib.dueOn', { date: l.dueOn })}
                {l.overdueDays > 0 && (
                  <span className="ml-2 font-medium text-red-700">
                    {t('lib.overdueDays', { days: l.overdueDays })}
                  </span>
                )}
              </span>
              {issuing && (
                <span className="flex gap-2">
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('lib.return')}: ${l.title}`}
                    disabled={returnLoan.isPending}
                    onClick={() => returnLoan.mutate(l.id)}
                  >
                    {t('lib.return')}
                  </button>
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('lib.renew')}: ${l.title}`}
                    disabled={act.isPending}
                    onClick={() => act.mutate({ id: l.id, what: 'renew' })}
                  >
                    {t('lib.renew')}
                  </button>
                  <button
                    type="button"
                    className="rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                    aria-label={`${t('lib.lost')}: ${l.title}`}
                    disabled={act.isPending}
                    onClick={() =>
                      window.confirm(t('common.confirmDelete', { name: l.title })) &&
                      act.mutate({ id: l.id, what: 'lost' })
                    }
                  >
                    {t('lib.lost')}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
        {loans.data && loans.data.meta.totalPages > 1 && (
          <Pager page={page} totalPages={loans.data.meta.totalPages} onPage={setPage} />
        )}
      </div>
    </section>
  );
}
